import { RootState } from '@reducers/index';
import {
    getChallengeGroupsFetchError,
    getChallengeGroupsIsFetching,
} from '@selectors/challengeGroups';
import {
    getChallengesFetchError,
    getChallengesForGroupID,
    getChallengesIsFetching,
} from '@selectors/challenges';
import dayjs from 'dayjs';
import DataCheck from 'lib/src/components/common/DataCheck';
import Table from 'lib/src/components/table/Table';
import { siteNames } from 'lib/src/shared/enums/site';
import { Column } from 'lib/src/types/table';
import { isEmpty } from 'lib/src/utils/generic';
import { useSelector } from 'react-redux';
import { getDateRangeFromChallenge } from '../../../utils/generic';
import { Challenge, challengeStageValues } from '../../../types/shared/Challenge';
import { ChallengeGroup } from '../../../types/shared/ChallengeGroup';
import ChallengeGroupChallengesTableActions from './ChallengeGroupChallengesTableActions';

const ChallengeGroupChallengesTable = ({ group }: ChallengeGroupChallengesTableProps) => {
    const challenges = useSelector((state: RootState) => getChallengesForGroupID(state, group.id));
    const isFetchingChallenges = useSelector(getChallengesIsFetching);
    const isFetchingGroup = useSelector(getChallengeGroupsIsFetching);
    const challengeFetchError = useSelector(getChallengesFetchError);
    const groupFetchError = useSelector(getChallengeGroupsFetchError);

    const sortedChallenges = challenges.sort((a, b) =>
        dayjs(b.startDate || undefined).diff(dayjs(a.startDate || undefined)),
    ); // sort by start date

    const columns: Column<Challenge>[] = [
        {
            key: 1,
            heading: 'Name',
            getValue: row => row.name,
        },
        {
            key: 2,
            heading: 'Start Date',
            getValue: row =>
                row.startDate ? dayjs(row.startDate).format('DD/MM/YYYY') : 'Unpublished',
        },
        {
            key: 3,
            heading: 'End Date',
            getValue: row => {
                const { end } = getDateRangeFromChallenge(row);
                return dayjs(end).format('DD/MM/YYYY');
            },
        },
        {
            key: 4,
            heading: 'Site permissions',
            getValue: row => siteNames[row.site],
        },
        {
            key: 5,
            heading: 'Stage',
            getValue: row => challengeStageValues[`${row.challengeStage}`] || '-',
        },
        {
            key: 6,
            heading: '',
            getValue: row => {
                return <ChallengeGroupChallengesTableActions challenge={row} />;
            },
        },
    ];

    return (
        <DataCheck
            dataExists={!isEmpty(challenges)}
            isFetching={isFetchingChallenges || isFetchingGroup}
            noDataMessage="No Challenges found."
            error={challengeFetchError ?? groupFetchError}
        >
            <Table rows={sortedChallenges} columns={columns} />
        </DataCheck>
    );
};

interface ChallengeGroupChallengesTableProps {
    group: ChallengeGroup;
}

export default ChallengeGroupChallengesTable;
